import { useState } from 'react'

import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { AlertTriangle, ArrowRight, Calendar, ChevronDown, ChevronUp, Clock } from 'lucide-react'

import { CheckoutChange } from '@/hooks/use-checkout-changes'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { serviceTypeColors } from '@/shared/utils/serviceTypeColors'
import { HairdressingServiceType } from '@monorepo/functions/src/types/services'

interface CheckoutChangesNotificationBannerProps {
    changes: CheckoutChange[]
    onViewChange: (change: CheckoutChange) => void
    onAcknowledgeChange?: (change: CheckoutChange) => void
}

const serviceTypeLabels: Record<HairdressingServiceType, string> = {
    bath_and_brush: 'Baño y cepillado',
    bath_and_trim: 'Baño y corte',
    stripping: 'Stripping',
    deshedding: 'Deslanado',
    brushing: 'Cepillado',
    spa: 'Spa',
    spa_ozone: 'Spa con ozono',
    knots: 'Nudos',
    nail_trimming: 'Extremadamente sucio',
}

export function CheckoutChangesNotificationBanner({
    changes,
    onViewChange,
    onAcknowledgeChange,
}: CheckoutChangesNotificationBannerProps) {
    const [isExpanded, setIsExpanded] = useState(false)

    if (changes.length === 0) return null

    const handleToggleExpansion = () => {
        setIsExpanded(!isExpanded)
    }

    return (
        <div className='w-full border-b border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 shadow-sm'>
            {/* Banner comprimido - Clickeable */}
            <div
                className='cursor-pointer px-6 py-4 transition-all duration-200 hover:bg-gradient-to-r hover:from-amber-100/70 hover:to-yellow-100/70'
                onClick={handleToggleExpansion}
            >
                <div className='flex items-center justify-between'>
                    <div className='flex items-center gap-3'>
                        <div className='flex h-8 w-8 items-center justify-center rounded-full bg-amber-100'>
                            <AlertTriangle className='h-4 w-4 text-amber-600' />
                        </div>
                        <div className='flex flex-col'>
                            <span className='text-sm font-semibold text-amber-900'>
                                {changes.length === 1
                                    ? 'Cambio de checkout pendiente'
                                    : `${changes.length} cambios de checkout pendientes`}
                            </span>
                            <span className='text-xs text-amber-700'>Haz clic para revisar los detalles</span>
                        </div>
                    </div>
                    <div className='flex items-center gap-3'>
                        <Badge variant='secondary' className='bg-amber-100 font-medium text-amber-800'>
                            {changes.length} cambio{changes.length !== 1 ? 's' : ''}
                        </Badge>
                        <div className='rounded-full bg-amber-100 p-1 transition-transform duration-200'>
                            {isExpanded ? (
                                <ChevronUp className='h-4 w-4 text-amber-600' />
                            ) : (
                                <ChevronDown className='h-4 w-4 text-amber-600' />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Contenido expandido */}
            <div
                className={cn(
                    'container mx-auto transition-all duration-300 ease-in-out',
                    isExpanded ? 'max-h-[500px] overflow-y-auto pb-4' : 'max-h-0 overflow-hidden',
                )}
            >
                <div className='space-y-3 px-6'>
                    <div className='h-px bg-gradient-to-r from-transparent via-amber-200 to-transparent' />
                    {changes.map((change, index) => (
                        <div
                            key={change.reservationId}
                            className='group relative overflow-hidden rounded-xl border border-amber-200/60 bg-white/80 p-4 shadow-sm transition-all duration-200 hover:border-amber-300 hover:bg-white hover:shadow-md'
                            style={{
                                animationDelay: `${index * 100}ms`,
                                animation: isExpanded ? 'slideInUp 0.3s ease-out forwards' : 'none',
                            }}
                        >
                            {/* Línea de acento lateral */}
                            <div className='absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-amber-400 to-amber-600' />

                            <div className='ml-2 space-y-2'>
                                {/* Primera línea: Mascotas, servicios y acciones */}
                                <div className='flex items-center justify-between gap-4'>
                                    <div className='flex min-w-0 flex-1 items-center gap-3'>
                                        <h3 className='truncate text-lg font-semibold text-gray-900'>
                                            {change.petNames.join(', ')}
                                        </h3>
                                        <div className='flex flex-wrap gap-1.5'>
                                            {change.services.map((service, serviceIndex) => (
                                                <Badge
                                                    key={serviceIndex}
                                                    variant='outline'
                                                    className={cn(
                                                        'whitespace-nowrap border-[1.5px] px-2 py-0.5 text-xs font-medium',
                                                        serviceTypeColors[service],
                                                    )}
                                                >
                                                    {serviceTypeLabels[service]}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                    <div className='flex flex-shrink-0 gap-2'>
                                        <Button
                                            variant='outline'
                                            size='sm'
                                            className='border-amber-300 bg-white text-amber-700 transition-all hover:bg-amber-50 hover:text-amber-800 hover:shadow-sm'
                                            onClick={e => {
                                                e.stopPropagation()
                                                onViewChange(change)
                                            }}
                                        >
                                            Revisar
                                        </Button>
                                        {onAcknowledgeChange && (
                                            <Button
                                                variant='outline'
                                                size='sm'
                                                className='border-emerald-300 bg-emerald-50 text-emerald-700 transition-all hover:bg-emerald-100 hover:text-emerald-800 hover:shadow-sm'
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    onAcknowledgeChange(change)
                                                }}
                                            >
                                                Aceptar
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {/* Segunda línea: Cambio de horario */}
                                <div className='flex items-center gap-3 text-sm'>
                                    <div className='flex items-center gap-2 text-gray-700'>
                                        <Clock className='h-4 w-4 text-gray-500' />
                                        <span className='font-medium'>{change.currentTime}</span>
                                        <Calendar className='h-4 w-4 text-gray-500' />
                                        <span className='font-medium'>
                                            {format(new Date(change.currentDate), "d 'de' MMMM", {
                                                locale: es,
                                            })}
                                        </span>
                                    </div>
                                    <ArrowRight className='h-4 w-4 text-emerald-600' />
                                    <div className='flex items-center gap-2 text-emerald-800'>
                                        <Clock className='h-4 w-4 text-emerald-600' />
                                        <span className='font-semibold'>{change.newTime}</span>
                                        <Calendar className='h-4 w-4 text-emerald-600' />
                                        <span className='font-semibold'>
                                            {format(new Date(change.newDate), "d 'de' MMMM", { locale: es })}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
